import React, {
    ElementType,
    MutableRefObject,
    ReactNode,
    RefObject,
    createContext,
    useContext,
    useId,
    useLayoutEffect,
    useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useModalRoot } from 'hooks/usePortalRoot/usePortalRoot';
import { inertBackground } from 'hooks/useInertBackground/useInertBackground';
import { MAIN_CONTENT_ID } from 'hooks/useRouteFocus/useRouteFocus';

type DialogEntry = {
    id: string;
    parentId: string | null;
    element: HTMLElement;
    onCloseRef: MutableRefObject<(() => void) | undefined>;
    closeOnEscapeRef: MutableRefObject<boolean>;
};

export const FOCUSABLE = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]',
]
    .map((selector) => `${selector}:not([tabindex="-1"])`)
    .join(', ');

const stack: DialogEntry[] = [];

const register = (entry: DialogEntry) => {
    const childIndex = stack.findIndex((existing) => existing.parentId === entry.id);

    if (childIndex === -1) {
        stack.push(entry);
    } else {
        stack.splice(childIndex, 0, entry);
    }
};

const unregister = (id: string) => {
    const index = stack.findIndex((entry) => entry.id === id);

    if (index !== -1) {
        stack.splice(index, 1);
    }
};

const getTopmost = (): DialogEntry | undefined => stack[stack.length - 1];

const getEntriesAbove = (id: string): DialogEntry[] => stack.slice(stack.findIndex((entry) => entry.id === id) + 1);

const onEscape = (event: KeyboardEvent) => {
    const topmost = getTopmost();

    if (event.key !== 'Escape' || !topmost) return;

    event.preventDefault();
    event.stopPropagation();

    if (topmost.closeOnEscapeRef.current) {
        topmost.onCloseRef.current?.();
    }
};

let escapeListener = false;

const initEscapeListener = () => {
    if (escapeListener) return;

    escapeListener = true;
    window.addEventListener('keydown', onEscape, true);
};

const isFocusable = (element: HTMLElement | null): element is HTMLElement =>
    !!element && element.isConnected && !element.closest('[inert]');

const ParentDialogContext = createContext<string | null>(null);

type ModalDialogProps = {
    as?: ElementType;
    open?: boolean;
    container?: HTMLElement | null;
    role?: 'dialog' | 'alertdialog';
    onClose?: () => void;
    closeOnEscape?: boolean;
    initialFocusRef?: RefObject<HTMLElement | null>;
    dialogRef?: MutableRefObject<HTMLElement | null>;
    portal?: boolean;
    children?: ReactNode;
    [prop: string]: any;
};

export const ModalDialog = ({
    as: Component = 'div',
    open = true,
    container,
    role = 'dialog',
    onClose,
    closeOnEscape = true,
    initialFocusRef,
    dialogRef,
    portal = true,
    children,
    ...rest
}: ModalDialogProps) => {
    const id = useId();
    const parentId = useContext(ParentDialogContext);
    const modalRoot = useModalRoot();
    const elementRef = useRef<HTMLElement>(null);
    const onCloseRef = useRef(onClose);
    const closeOnEscapeRef = useRef(closeOnEscape);

    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;

    useLayoutEffect(() => {
        const element = elementRef.current;

        if (!open || !element) return undefined;

        initEscapeListener();

        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

        register({ id, parentId, element, onCloseRef, closeOnEscapeRef });

        const releaseInert = inertBackground(
            element,
            getEntriesAbove(id).map((entry) => entry.element)
        );

        if (getTopmost()?.id === id) {
            const preferred = initialFocusRef?.current;
            const target =
                (preferred?.matches(FOCUSABLE) ? preferred : preferred?.querySelector<HTMLElement>(FOCUSABLE)) ??
                element.querySelector<HTMLElement>(FOCUSABLE) ??
                element;

            target.focus();
        }

        return () => {
            const wasTopmost = getTopmost()?.id === id;

            releaseInert();
            unregister(id);

            if (!wasTopmost) return;

            // return focus to whereever it was before
            queueMicrotask(() => {
                const topmost = getTopmost();

                if (topmost) {
                    (isFocusable(opener) && topmost.element.contains(opener) ? opener : topmost.element).focus();
                } else if (isFocusable(opener)) {
                    opener.focus();
                } else {
                    document.getElementById(MAIN_CONTENT_ID)?.focus();
                }
            });
        };
    }, [open]);

    const dialog = (
        <Component
            role={open ? role : undefined}
            aria-modal={open ? 'true' : undefined}
            tabIndex={-1}
            inert={!open || undefined}
            {...rest}
            ref={(node: HTMLElement | null) => {
                elementRef.current = node;

                if (dialogRef) {
                    dialogRef.current = node;
                }
            }}
        >
            <ParentDialogContext.Provider value={id}>{children}</ParentDialogContext.Provider>
        </Component>
    );

    return portal ? createPortal(dialog, container ?? modalRoot) : dialog;
};
